import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { SelectedContact } from "@newPages/contact/ContactPage";
import { useBoolean, UseBoolean } from "@newHooks/common/useBoolean";
import { ContactSummary } from "@maven-rest/docs/Contact.dto";

type ContactContextType = {
  dialogStatus: {
    openAddContactDialog: UseBoolean;
    openAddContactTemplateDialog: UseBoolean;
    openAddContactUsersToGroupsDialog: UseBoolean;
    openDetailContactDialog: UseBoolean;
    openEditContactDialog: UseBoolean;
    openDeleteContactDialog: UseBoolean;
    openDeleteContactGroupDialog: UseBoolean;
  };
  selectedContactRows: ContactSummary[];
  onSelectedContactRows: (contacts: ContactSummary[]) => void;
  selectedContact: SelectedContact | null;
  setSelectedContact: (contact: SelectedContact | null) => void;
  isSelectedContactsSameScope: boolean;
};

const ContactContext = createContext<ContactContextType | null>(null);

const useContactContext = () => {
  const context = useContext(ContactContext);
  if (!context) {
    throw new Error("useContactContext must be used within a ContactProvider");
  }
  return context;
};

function ContactProvider({ children }: { children: React.ReactNode }) {
  const [selectedContactRows, setSelectedContactRows] = useState<ContactSummary[]>([]);
  const [selectedContact, setSelectedContact] = useState<SelectedContact | null>(null);

  const openAddContactDialog = useBoolean(false);
  const openAddContactTemplateDialog = useBoolean(false);
  const openAddContactUsersToGroupsDialog = useBoolean(false);
  const openDetailContactDialog = useBoolean(false);
  const openEditContactDialog = useBoolean(false);
  const openDeleteContactDialog = useBoolean(false);
  const openDeleteContactGroupDialog = useBoolean(false);

  const isSelectedContactsSameScope =
    !!selectedContactRows.length && selectedContactRows.every((contact) => contact.scope === selectedContactRows[0].scope);

  const dialogStatus = useMemo(
    () => ({
      openAddContactDialog,
      openAddContactTemplateDialog,
      openAddContactUsersToGroupsDialog,
      openDetailContactDialog,
      openEditContactDialog,
      openDeleteContactDialog,
      openDeleteContactGroupDialog,
    }),
    [
      openAddContactDialog,
      openAddContactTemplateDialog,
      openAddContactUsersToGroupsDialog,
      openDetailContactDialog,
      openEditContactDialog,
      openDeleteContactDialog,
      openDeleteContactGroupDialog,
    ]
  );

  const handleSelectedContactRows = useCallback((contacts: ContactSummary[]) => {
    setSelectedContactRows(contacts);
  }, []);

  const contextValue = useMemo(
    () => ({
      dialogStatus,
      selectedContactRows,
      onSelectedContactRows: handleSelectedContactRows,
      selectedContact,
      setSelectedContact,
      isSelectedContactsSameScope,
    }),
    [dialogStatus, selectedContactRows, selectedContact, handleSelectedContactRows, isSelectedContactsSameScope]
  );

  return <ContactContext.Provider value={contextValue}>{children}</ContactContext.Provider>;
}

export { useContactContext, ContactProvider };
